import React, { useMemo } from 'react';

import SelectFormField from '@components/form-fields/SelectFormField';
import SwitchFormField from '@components/form-fields/SwitchFormField';
import DeleteIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { MAIN_THEME } from '@styles';

import { AmendmentPolicyCoverForm } from '../types/getAmendmentsDetailsResponse.types';

import { FormikErrors, FormikTouched } from 'formik';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '14px',
    position: 'relative'
  },
  // TODO: Adjust position of isRemoved toggle when design instructions are available
  row: {
    display: 'grid',
    gap: 8,
    gridTemplateColumns: '1fr 200px',
    alignItems: 'end'
  },
  removeBtn: {
    position: 'absolute',
    top: 6,
    right: 6
  }
}));

type Props = {
  row: AmendmentPolicyCoverForm;
  rowIndex: number;

  disabledForm: boolean;
  allowPlanCoverEdits: boolean;
  amendmentStatus: string;

  coverOptions: Record<string, string>;
  disablePolicyCoverSelect: boolean;
  onRemove?: () => void;

  touched: FormikTouched<{ covers: AmendmentPolicyCoverForm[] }>;
  errors: FormikErrors<{ covers: AmendmentPolicyCoverForm[] }>;

  setFieldValue: (path: string, value: any, shouldValidate?: boolean) => void;
  setFieldTouched: (
    path: string,
    touched?: boolean,
    shouldValidate?: boolean
  ) => void;
  handleBlur: (fieldOrEvent: any) => void;
  onBlurPolicyCover: (selectedId?: string) => void;
};

const MedicalPolicyCoverRowAtom: React.FC<Props> = ({
  row,
  rowIndex,

  disabledForm,
  allowPlanCoverEdits,
  amendmentStatus,

  coverOptions,
  disablePolicyCoverSelect,
  onRemove,

  touched,
  errors,

  setFieldValue,
  setFieldTouched,
  handleBlur,
  onBlurPolicyCover
}) => {
  const { classes } = useStyles();
  const base = `covers[${rowIndex}]`;

  const canToggleRemoved = useMemo(
    () => !row.isMandatory && !row.isNew,
    [row.isMandatory, row.isNew]
  );

  const disabledIsRemoved = useMemo(() => {
    const isClosed = ['COMPLETED', 'CANCELLED', 'CANCELED'].includes(
      (amendmentStatus || '').toUpperCase()
    );

    return disabledForm || isClosed || !allowPlanCoverEdits;
  }, [disabledForm, amendmentStatus, allowPlanCoverEdits]);

  const err = (key: keyof AmendmentPolicyCoverForm) =>
    (touched?.covers?.[rowIndex] as any)?.[key]
      ? (errors?.covers?.[rowIndex] as any)?.[key]
      : '';

  return (
    <div className={classes.container}>
      {row.isNew && !row.isMandatory && onRemove && (
        <Tooltip title="Remove Policy Plan Cover">
          <IconButton
            size="small"
            className={classes.removeBtn}
            onClick={onRemove}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <div className={classes.row}>
        <SelectFormField
          name={`${base}.policyCover`}
          title="Policy Cover*"
          placeholder="Policy Cover"
          value={row.policyCover}
          selectOptions={coverOptions}
          onBlur={(_, e) => {
            handleBlur(e);
          }}
          onChange={(v: any) => {
            const sel = String(v);
            setFieldValue(`${base}.policyCover`, sel, false);
            setFieldTouched(`${base}.policyCover`, true, false);
            onBlurPolicyCover(sel);
          }}
          error={err('policyCover')}
          disabled={disablePolicyCoverSelect}
          material
        />

        {canToggleRemoved && (
          <SwitchFormField
            name={`${base}.isRemoved`}
            title="Is Removed"
            placeholder="Is Removed"
            value={row.isRemoved === true}
            onChange={(_, val: boolean) => {
              setFieldValue(`${base}.isRemoved`, val ? true : false, false);
              setFieldTouched(`${base}.isRemoved`, true, false);
            }}
            onBlur={() => handleBlur(`${base}.isRemoved`)}
            disabled={disabledIsRemoved}
            material
          />
        )}
      </div>
    </div>
  );
};

export { MedicalPolicyCoverRowAtom };
